/**
 * Run `onTick` every `ms` while the page is visible (plan 7.7: the Calls tab
 * polls GET /calls every 60 s while visible; the LMS convention, LmsProvider,
 * rather than a sixth long-lived stream).
 *
 * Hidden: the interval stops, so a background tab asks nothing. Shown again:
 * one tick at once (the list may be minutes old), then the rhythm restarts.
 * The first load is the caller's own; this hook only repeats it. `onTick` may
 * change identity every render: the latest one is always called.
 */
import { useEffect, useRef, useState } from 'react';
import { pageVisible } from './callMeta';

export function useVisiblePoll(onTick: () => void, ms: number, enabled = true): void {
  const tickRef = useRef(onTick);
  tickRef.current = onTick;
  const [visible, setVisible] = useState(pageVisible);
  const wasHidden = useRef(!pageVisible());

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const onChange = () => setVisible(pageVisible());
    document.addEventListener('visibilitychange', onChange);
    return () => document.removeEventListener('visibilitychange', onChange);
  }, []);

  useEffect(() => {
    if (!enabled || !visible) {
      if (!visible) wasHidden.current = true;
      return undefined;
    }
    // Shown again after being hidden: catch up at once.
    if (wasHidden.current) {
      wasHidden.current = false;
      tickRef.current();
    }
    const timer = window.setInterval(() => {
      if (pageVisible()) tickRef.current();
    }, ms);
    return () => window.clearInterval(timer);
  }, [enabled, visible, ms]);
}

export default useVisiblePoll;
